import { FC, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { PageTitle } from '../../../_metronic/layout/core';
import DateRangeSelector from '../../components/DateRangeSelector';
import {
  TachoOrderList,
  TachoBulkActionConfig,
  TachoBulkActionsEnum,
  listTachoOrders,
  performTachoBulkAction,
} from '../../modules/tacho-orders';
import {
  ExternalOrderModel,
  ExternalOrderStateEnum,
} from '../../modules/external-orders/models/ExternalOrderModel';

const BULK_ACTIONS: TachoBulkActionConfig[] = [
  {
    action: TachoBulkActionsEnum.PREPARE_ORDERS,
    i18nKey: 'prepareOrders',
    icon: 'fa-magic',
  },
];

const statusOptions = (intl: ReturnType<typeof useIntl>) => [
  { value: null, label: intl.formatMessage({ id: 'tachoOrders.allStatuses' }) },
  {
    value: ExternalOrderStateEnum.RECEIVED,
    label: intl.formatMessage({ id: 'tachoOrders.statusReceived' }),
  },
  {
    value: ExternalOrderStateEnum.PROCESSING,
    label: intl.formatMessage({ id: 'tachoOrders.statusProcessing' }),
  },
  {
    value: ExternalOrderStateEnum.PROCESSED,
    label: intl.formatMessage({ id: 'tachoOrders.statusProcessed' }),
  },
  {
    value: ExternalOrderStateEnum.ERROR,
    label: intl.formatMessage({ id: 'tachoOrders.statusError' }),
  },
  {
    value: ExternalOrderStateEnum.CANCELLED,
    label: intl.formatMessage({ id: 'tachoOrders.statusCancelled' }),
  },
];

const TachoOrdersPage: FC = () => {
  const intl = useIntl();
  const [orders, setOrders] = useState<Array<ExternalOrderModel>>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const [dateRange, setDateRange] = useState<{
    since: Date | null;
    until: Date | null;
  }>({ since: null, until: null });
  const [status, setStatus] = useState<ExternalOrderStateEnum | null>(null);

  const formatDate = (date: Date | null) =>
    date
      ? date.toLocaleDateString('en-CA', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        })
      : undefined;

  async function fetchOrders() {
    try {
      setLoading(true);
      const { data } = await listTachoOrders({
        page,
        date__gte: formatDate(dateRange.since),
        date__lte: formatDate(dateRange.until),
        status: status || undefined,
      });
      setOrders(data?.results);
      setCount(data?.count);
    } catch (error) {
      console.warn(error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchOrders();
  }, [page, dateRange, status]);

  async function handleBulkAction(
    action: TachoBulkActionsEnum,
    orderIds: Array<number>
  ) {
    await performTachoBulkAction({
      orders: orderIds,
      action,
      date__gte: formatDate(dateRange.since),
      date__lte: formatDate(dateRange.until),
      status: status || undefined,
    });
    fetchOrders();
  }

  return (
    <>
      <PageTitle>{intl.formatMessage({ id: 'tachoOrders.title' })}</PageTitle>
      <TachoOrderList
        orders={orders}
        page={page}
        count={count}
        onPageChange={(p) => setPage(p)}
        loading={loading}
        displayBulkActions={true}
        bulkActions={BULK_ACTIONS}
        onBulkAction={handleBulkAction}
      >
        <div className="col-auto">
          <DateRangeSelector
            label={intl.formatMessage({ id: 'tachoOrders.filterByDate' })}
            onSelected={(since, until) => {
              setPage(1);
              setDateRange({ since, until });
            }}
            initialSince={dateRange.since}
            initialUntil={dateRange.until}
          />
        </div>
        <div className="col-auto d-flex flex-column">
          <label className="form-label fw-bold">
            {intl.formatMessage({ id: 'tachoOrders.filterByStatus' })}
          </label>
          <select
            className="form-select w-200px"
            value={status ?? ''}
            onChange={(e) => {
              setPage(1);
              setStatus((e.target.value as ExternalOrderStateEnum) || null);
            }}
          >
            {statusOptions(intl).map((opt) => (
              <option key={opt.value ?? ''} value={opt.value ?? ''}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </TachoOrderList>
    </>
  );
};

export { TachoOrdersPage };
